import { useEffect } from "react";
import { useAccountPath } from "@/providers/AccountProvider";
import { useNavigate } from "react-router-dom";
import { Command } from "cmdk";
import { useUIStore } from "@/store/useUIStore";
import { mayLeaveCanvas } from "@/components/flow/useLeaveGuard";

// Global Cmd+K / Ctrl+K palette -- cross-module navigation without touching the sidebar.
//
// Chrome follows DIALOG_STYLE.md: heavy scrim, panel one step lighter than the page,
// hairline border, large radius, no shadow. Type scale follows appsidebar4stylespec.md
// (13px actionable, 11px section labels), so the palette reads as part of the nav
// system rather than as a separate widget.

interface PaletteCommand {
  group: string;
  label: string;
  to: string;
  keywords: string;
}

const COMMANDS: PaletteCommand[] = [
  { group: "Insights", label: "Executive overview", to: "/dashboard", keywords: "dashboard kpi roas spend revenue metrics" },
  { group: "Conversations", label: "All channels", to: "/inbox", keywords: "inbox unified messages chat" },
  { group: "Conversations", label: "WhatsApp", to: "/inbox/whatsapp", keywords: "inbox whatsapp wa messages" },
  { group: "Conversations", label: "Instagram", to: "/inbox/instagram", keywords: "inbox instagram ig dm messages" },
  { group: "Conversations", label: "Messenger", to: "/inbox/messenger", keywords: "inbox messenger facebook fb messages" },
  { group: "Revenue", label: "Opportunities", to: "/opportunities", keywords: "kanban deals opportunities stages board pipeline" },
  { group: "Revenue", label: "Pipelines", to: "/pipeline", keywords: "manage pipeline stages create rename delete configure" },
  { group: "Revenue", label: "Contacts", to: "/contacts", keywords: "crm leads people customers table" },
  { group: "Social", label: "WhatsApp Broadcast", to: "/broadcast", keywords: "broadcast whatsapp bulk message campaign template smart list segment" },
  { group: "Social", label: "Composer", to: "/social/composer", keywords: "post publish facebook page instagram caption" },
  { group: "Social", label: "Engagement", to: "/social/engagement", keywords: "tracked posts reach likes metrics insights" },
  { group: "Automation", label: "Agent studio", to: "/agent-studio", keywords: "ai agent prompt knowledge guardrails" },
  { group: "Automation", label: "Automation rules", to: "/agent-studio/rules", keywords: "rules conditions tags routing if then" },
  { group: "Automation", label: "Form submissions", to: "/agent-studio/form-submissions", keywords: "whatsapp forms flows responses answers submitted" },
  { group: "Insights", label: "Campaign performance", to: "/performance", keywords: "campaigns spend roas table" },
  { group: "Insights", label: "All ads", to: "/ads", keywords: "ads creatives whatsapp integration spend" },
  {
    group: "Workspace",
    label: "Accounts & assets",
    to: "/connections",
    keywords: "connect meta whatsapp facebook instagram integration",
  },
  { group: "Workspace", label: "Catalog", to: "/catalog", keywords: "products catalog commerce items" },
  { group: "Workspace", label: "Shop orders", to: "/commerce", keywords: "commerce orders shop merchant" },
  { group: "Workspace", label: "Lead forms", to: "/lead-forms", keywords: "instant forms leadgen page" },
  {
    group: "Workspace",
    label: "Custom fields",
    to: "/custom-fields",
    keywords: "attributes fields schema contact opportunity",
  },
  { group: "Workspace", label: "Preferences", to: "/settings", keywords: "settings config workspace theme" },
];

const GROUP_ORDER = ["Insights", "Conversations", "Revenue", "Social", "Automation", "Workspace"];

const GROUP_HEADING_CLASS =
  "mb-2 [&_[cmdk-group-heading]]:mb-1 [&_[cmdk-group-heading]]:px-3 [&_[cmdk-group-heading]]:text-[11px] [&_[cmdk-group-heading]]:font-medium [&_[cmdk-group-heading]]:uppercase [&_[cmdk-group-heading]]:tracking-[0.05em] [&_[cmdk-group-heading]]:text-n-fg-faint";

const ITEM_CLASS =
  "flex h-8 cursor-pointer items-center justify-between rounded-[var(--radius-ctl)] px-3 text-[13px] text-n-fg-muted transition-colors duration-150 ease-out data-[selected=true]:bg-n-hover data-[selected=true]:text-n-fg";

interface CommandPaletteProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export function CommandPalette({ open, onOpenChange }: CommandPaletteProps) {
  const accountPath = useAccountPath();
  const navigate = useNavigate();
  // Light/dark is the USER's own choice, kept apart from the workspace's colours: switching
  // it repaints the same brand the other way round rather than replacing it.
  const isDark = useUIStore((s) => s.mode === "dark");
  const setMode = useUIStore((s) => s.setMode);

  useEffect(() => {
    function onKeyDown(e: KeyboardEvent) {
      if (e.key.toLowerCase() === "k" && (e.metaKey || e.ctrlKey)) {
        e.preventDefault();
        onOpenChange(!open);
      }
    }
    document.addEventListener("keydown", onKeyDown);
    return () => document.removeEventListener("keydown", onKeyDown);
  }, [open, onOpenChange]);

  function run(action: () => void) {
    onOpenChange(false);
    action();
  }

  return (
    <Command.Dialog
      open={open}
      onOpenChange={onOpenChange}
      label="Command palette"
      className="fixed inset-0 z-[80] bg-black/60"
      contentClassName="fixed left-1/2 top-[12vh] w-[min(560px,calc(100vw-2rem))] -translate-x-1/2 overflow-hidden rounded-[var(--radius-card)] border-hairline border-n-line bg-n-surface"
    >
      <div className="border-b-[0.8px] border-n-line px-4">
        <Command.Input
          autoFocus
          placeholder="Search modules…"
          className="h-12 w-full bg-transparent text-[13px] text-n-fg outline-none placeholder:text-n-fg-faint"
        />
      </div>

      <Command.List className="max-h-[320px] overflow-y-auto p-2">
        <Command.Empty className="px-3 py-6 text-center text-[13px] text-n-fg-faint">
          No matching command.
        </Command.Empty>

        {GROUP_ORDER.map((group) => {
          const items = COMMANDS.filter((c) => c.group === group);
          if (items.length === 0) return null;
          return (
            <Command.Group key={group} heading={group} className={GROUP_HEADING_CLASS}>
              {items.map((command) => (
                <Command.Item
                  key={command.to}
                  value={`${command.label} ${command.keywords}`}
                  onSelect={() =>
                    run(() => {
                      // An automation canvas with unsaved edits asks first: this navigates
                      // without a link, so the canvas's own guard never sees it.
                      if (mayLeaveCanvas()) navigate(command.to);
                    })
                  }
                  className={ITEM_CLASS}
                >
                  <span>{command.label}</span>
                </Command.Item>
              ))}
            </Command.Group>
          );
        })}

        <Command.Group heading="Actions" className={GROUP_HEADING_CLASS}>
          <Command.Item
            value="toggle theme dark light appearance palette branding"
            onSelect={() => run(() => setMode(isDark ? "light" : "dark"))}
            className={ITEM_CLASS}
          >
            <span>Switch to {isDark ? "light" : "dark"} mode</span>
          </Command.Item>
          <Command.Item
            value="branding colors theme customize palette"
            onSelect={() =>
              run(() => {
                if (mayLeaveCanvas()) navigate(accountPath("/settings/branding"));
              })
            }
            className={ITEM_CLASS}
          >
            <span>Workspace branding</span>
          </Command.Item>
        </Command.Group>
      </Command.List>
    </Command.Dialog>
  );
}
